import * as React from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useForm, Controller } from 'react-hook-form/dist/index.ie11';

import TextField from 'crehana-ui/TextField';
import { TextFieldProps } from 'crehana-ui/TextField/types';
import PrimaryButton from 'crehana-ui/Buttons/PrimaryButton';

import { PATTERNS } from 'Jsx/Utils/validations';
import { LOGIN_TRANSLATION_KEY } from 'Jsx/Utils/transformKeys';
import getQueryParam from 'Jsx/Utils/getQueryParam';
import getNextUrl from 'Jsx/Utils/getNextUrl';
import mixpanel from 'Jsx/Utils/mixpanelTracking';
import { sendRegisterTrackToGoogleAdsAndAnalytics } from 'Jsx/Auth/utils';

import { useTranslation } from 'Jsx/Shared/Hooks/useTranslation';
import localeEs from 'Jsx/MainMenu/locales/es';
import EVENTS from 'Jsx/Auth/constants/Tracking';

import { Me } from 'Jsx/Types/Me';

import LegalCheckbox, {
  LegalErrorMessage,
  rulesForRHFControllerComponent,
} from './components/LegalCheckbox';

import CREATE_USER_MUTATION from './graphql/CreateUser.graphql';
import VERIFY_USER_QUERY from './graphql/VerifyUser.graphql';

interface SignUpProps {
  giftcard?: boolean;
  track?: boolean;
  email?: string;
  customRegisterLabel?: string;
  nextUrl?: string;
  referFriendId?: number;
  customHandler?(params: {
    redirect: string;
    user: Pick<
      Me,
      'originalId' | 'firstName' | 'lastName' | 'phoneCode' | 'phoneNumber'
    >;
    isNewUser?: boolean;
  }): void;

  darkMode?: boolean;
  /**
   * Useful to render a custom footer (maybe you need to put some components between the button and the textfields)
   * The function receives the button and legalCheckbox jsx as params
   */
  renderFooter?: (jsxComponents: {
    button: React.ReactNode;
    legalCheckbox: React.ReactNode;
    /**
     *  Ex:
     *    const singleValue = getValues("test"); // "test-input"
     *    const singleValue = getValues(["test", "test1"]);
     * See more options:
     * https://react-hook-form.com/api/#getValues
     */
    getFormValues(fields: string | string[]): { [key: string]: string };
  }) => React.ReactNode;
  textFieldProps?: TextFieldProps;
  /**
   * Pass the page, section or component where this component is used.
   * This prop is used to send extra data to the mixpanel track "AUTH_REGISTER"
   * Ex: If you pass "catalog" the mixpanel track is sent as: `mixpanel(REGISTER, { isRenderedIn })`
   */
  isRenderedIn?: string;
  /**
   * custom button props
   */
  buttonProps?: {
    id?: string;
  };
  defaultEmailValue?: string;
  onSuccess?: () => void;
}

const EMAIL_PARAM = getQueryParam('email_user');

const SignUp: React.FC<SignUpProps> = props => {
  const {
    giftcard,
    referFriendId,
    nextUrl: _nextUrl,
    customRegisterLabel,
    customHandler,
    darkMode,
    renderFooter,
    textFieldProps,
    isRenderedIn,
    buttonProps,
    defaultEmailValue,
    onSuccess,
  } = props;

  const { t } = useTranslation(LOGIN_TRANSLATION_KEY);
  const [submitting, setSubmitting] = React.useState(false);

  const { refetch: validateExistedEmail } = useQuery(VERIFY_USER_QUERY, {
    skip: true,
  });
  const { register, handleSubmit, errors, control, getValues } = useForm({
    mode: 'onChange',
    defaultValues: {
      email: EMAIL_PARAM || defaultEmailValue || '',
      password: '',
      checkboxLegal: true,
    },
  });

  const nextUrl = _nextUrl || getNextUrl();
  const [createUserMutation] = useMutation(CREATE_USER_MUTATION);

  const onSubmit = async data => {
    mixpanel.track(EVENTS.SIGNUP_FORM_BUTTON_CLICK, { isRenderedIn });
    setSubmitting(true);
    const res = await createUserMutation({
      variables: {
        ...data,
        nextUrl,
        giftcardToken: giftcard ? getQueryParam('giftcard_token') : null,
        referFriendId,
      },
    });
    const { messages, success, redirect, user } = res.data.createUser;

    if (!success) {
      return {
        email: messages[0].message,
      };
    }

    sendRegisterTrackToGoogleAdsAndAnalytics();

    mixpanel.track(
      EVENTS.REGISTER,
      {
        shouldRequireExtraData: true,
        provider: 'email',
        isRenderedIn,
      },
      () => {
        if (onSuccess) {
          onSuccess();
        }
        if (customHandler) {
          customHandler({
            user,
            redirect,
            isNewUser: true,
          });
        } else {
          window.location.href = redirect;
        }
      },
    );
    setSubmitting(false);
  };

  const validatePassword = async value => {
    if (value.length >= 6 && value.length <= 20) {
      return true;
    }
    return t('AUTH_VALIDATION_PASSWORD_LENGTH', {
      defaultValue: localeEs.es.translation.AUTH_VALIDATION_PASSWORD_LENGTH,
    });
  };

  const validateEmail = async value => {
    const { data } = await validateExistedEmail({ value });

    if (data) {
      const { verifyUser } = data;
      const { messages, success } = verifyUser;

      if (success) return true;
      return t('AUTH_VALIDATION_EMAIL_IN_USE', messages[0].message);
    }
  };

  const legalCheckboxJsx = (
    <React.Fragment>
      <Controller
        control={control}
        name="checkboxLegal"
        render={({ onChange, onBlur, value }) => (
          <LegalCheckbox
            darkMode={darkMode}
            onChange={onChange}
            onBlur={onBlur}
            value={value}
          />
        )}
        rules={rulesForRHFControllerComponent(t)}
      />

      {errors.checkboxLegal && (
        <LegalErrorMessage message={errors.checkboxLegal.message} />
      )}
    </React.Fragment>
  );

  const buttonJsx = (
    <PrimaryButton
      full
      size="l"
      disabled={submitting}
      label={
        customRegisterLabel ||
        t('REGISTER', {
          defaultValue: localeEs.es.translation.REGISTER,
        })
      }
      type="submit"
      data-cy="submit"
      id="register"
      darkMode={darkMode}
      {...buttonProps}
    />
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-16">
        <TextField
          {...textFieldProps}
          name="email"
          label={t('REGISTER_EMAIL', {
            defaultValue: localeEs.es.translation.LOGIN_EMAIL,
          })}
          id="email"
          defaultValue={EMAIL_PARAM || defaultEmailValue}
          className="w-full"
          ref={register({
            required: {
              value: true,
              message: t('AUTH_VALIDATION_REQUIRED', {
                defaultValue: localeEs.es.translation.AUTH_VALIDATION_REQUIRED,
              }),
            },
            pattern: {
              value: PATTERNS.EMAIL,
              message: t('AUTH_VALIDATION_VALID_EMAIL', {
                defaultValue:
                  localeEs.es.translation.AUTH_VALIDATION_VALID_EMAIL,
              }),
            },
            validate: validateEmail,
          })}
          errorText={errors.email ? errors.email.message : undefined}
          darkMode={darkMode}
        />
      </div>
      <div className="mb-16">
        <TextField
          {...textFieldProps}
          name="password"
          type="password"
          label={t('LOGIN_PASSWORD', {
            defaultValue: localeEs.es.translation.LOGIN_PASSWORD,
          })}
          id="password"
          autoComplete="new-password"
          className="w-full"
          ref={register({
            required: {
              value: true,
              message: t('AUTH_VALIDATION_REQUIRED', {
                defaultValue: localeEs.es.translation.AUTH_VALIDATION_REQUIRED,
              }),
            },
            validate: validatePassword,
          })}
          errorText={errors.password ? errors.password.message : undefined}
          darkMode={darkMode}
        />
      </div>

      {renderFooter ? (
        renderFooter({
          legalCheckbox: legalCheckboxJsx,
          button: buttonJsx,
          getFormValues: getValues,
        })
      ) : (
        <React.Fragment>
          <div className="mb-16">{legalCheckboxJsx}</div>
          {buttonJsx}
        </React.Fragment>
      )}
    </form>
  );
};

export default SignUp;
